const inverse = (constant) => ({
  toBase: (value) => (value === 0 ? Number.NaN : constant / value),
  fromBase: (value) => (value === 0 ? Number.NaN : constant / value)
});

export const conversionGroups = [
  {
    id: "length",
    label: "Length",
    baseUnit: "meter",
    units: [
      { id: "mm", label: "Millimeters", short: "mm", factor: 0.001 },
      { id: "cm", label: "Centimeters", short: "cm", factor: 0.01 },
      { id: "m", label: "Meters", short: "m", factor: 1 },
      { id: "km", label: "Kilometers", short: "km", factor: 1000 },
      { id: "in", label: "Inches", short: "in", factor: 0.0254 },
      { id: "ft", label: "Feet", short: "ft", factor: 0.3048 },
      { id: "yd", label: "Yards", short: "yd", factor: 0.9144 },
      { id: "mi", label: "Miles", short: "mi", factor: 1609.344 },
      { id: "nmi", label: "Nautical miles", short: "nmi", factor: 1852 }
    ]
  },
  {
    id: "weight",
    label: "Weight",
    baseUnit: "kilogram",
    units: [
      { id: "mg", label: "Milligrams", short: "mg", factor: 0.000001 },
      { id: "g", label: "Grams", short: "g", factor: 0.001 },
      { id: "kg", label: "Kilograms", short: "kg", factor: 1 },
      { id: "t", label: "Metric tons", short: "t", factor: 1000 },
      { id: "oz", label: "Ounces", short: "oz", factor: 0.028349523125 },
      { id: "lb", label: "Pounds", short: "lb", factor: 0.45359237 },
      { id: "st", label: "Stone", short: "st", factor: 6.35029318 },
      { id: "short-ton", label: "US short tons", short: "US ton", factor: 907.18474 }
    ]
  },
  {
    id: "temperature",
    label: "Temperature",
    baseUnit: "celsius",
    units: [
      { id: "c", label: "Celsius", short: "deg C", toBase: (v) => v, fromBase: (v) => v },
      { id: "f", label: "Fahrenheit", short: "deg F", toBase: (v) => (v - 32) * 5 / 9, fromBase: (v) => v * 9 / 5 + 32 },
      { id: "k", label: "Kelvin", short: "K", toBase: (v) => v - 273.15, fromBase: (v) => v + 273.15 }
    ]
  },
  {
    id: "area",
    label: "Area",
    baseUnit: "square meter",
    units: [
      { id: "sqcm", label: "Square centimeters", short: "sq cm", factor: 0.0001 },
      { id: "sqm", label: "Square meters", short: "sq m", factor: 1 },
      { id: "sqkm", label: "Square kilometers", short: "sq km", factor: 1000000 },
      { id: "sqin", label: "Square inches", short: "sq in", factor: 0.00064516 },
      { id: "sqft", label: "Square feet", short: "sq ft", factor: 0.09290304 },
      { id: "sqyd", label: "Square yards", short: "sq yd", factor: 0.83612736 },
      { id: "acre", label: "Acres", short: "acre", factor: 4046.8564224 },
      { id: "hectare", label: "Hectares", short: "ha", factor: 10000 },
      { id: "pyeong", label: "Pyeong", short: "pyeong", factor: 3.305785124 }
    ]
  },
  {
    id: "volume",
    label: "Volume",
    baseUnit: "liter",
    units: [
      { id: "ml", label: "Milliliters", short: "ml", factor: 0.001 },
      { id: "l", label: "Liters", short: "L", factor: 1 },
      { id: "m3", label: "Cubic meters", short: "cu m", factor: 1000 },
      { id: "tsp", label: "US teaspoons", short: "tsp", factor: 0.00492892159375 },
      { id: "tbsp", label: "US tablespoons", short: "tbsp", factor: 0.01478676478125 },
      { id: "floz", label: "US fluid ounces", short: "fl oz", factor: 0.0295735295625 },
      { id: "cup", label: "US cups", short: "cup", factor: 0.2365882365 },
      { id: "pint", label: "US pints", short: "pt", factor: 0.473176473 },
      { id: "quart", label: "US quarts", short: "qt", factor: 0.946352946 },
      { id: "gal", label: "US gallons", short: "gal", factor: 3.785411784 },
      { id: "imp-gal", label: "Imperial gallons", short: "imp gal", factor: 4.54609 }
    ]
  },
  {
    id: "pressure",
    label: "Pressure",
    baseUnit: "kilopascal",
    units: [
      { id: "pa", label: "Pascals", short: "Pa", factor: 0.001 },
      { id: "kpa", label: "Kilopascals", short: "kPa", factor: 1 },
      { id: "mpa", label: "Megapascals", short: "MPa", factor: 1000 },
      { id: "bar", label: "Bar", short: "bar", factor: 100 },
      { id: "psi", label: "Pounds per square inch", short: "psi", factor: 6.894757293168361 },
      { id: "atm", label: "Atmospheres", short: "atm", factor: 101.325 },
      { id: "mmhg", label: "Millimeters of mercury", short: "mmHg", factor: 0.133322387415 }
    ]
  },
  {
    id: "speed",
    label: "Speed",
    baseUnit: "kilometer per hour",
    units: [
      { id: "kmh", label: "Kilometers per hour", short: "km/h", factor: 1 },
      { id: "mph", label: "Miles per hour", short: "mph", factor: 1.609344 },
      { id: "mps", label: "Meters per second", short: "m/s", factor: 3.6 },
      { id: "fps", label: "Feet per second", short: "ft/s", factor: 1.09728 },
      { id: "knot", label: "Knots", short: "kn", factor: 1.852 }
    ]
  },
  {
    id: "fuel",
    label: "Fuel Economy",
    baseUnit: "liters per 100 kilometers",
    units: [
      { id: "l100km", label: "Liters per 100 km", short: "L/100 km", toBase: (v) => v, fromBase: (v) => v },
      { id: "kml", label: "Kilometers per liter", short: "km/L", ...inverse(100) },
      { id: "mpg-us", label: "Miles per gallon US", short: "mpg US", ...inverse(235.214583) },
      { id: "mpg-imp", label: "Miles per gallon Imperial", short: "mpg imp", ...inverse(282.4809363) }
    ]
  },
  {
    id: "data",
    label: "Digital Storage",
    baseUnit: "byte",
    units: [
      { id: "b", label: "Bytes", short: "B", factor: 1 },
      { id: "kb", label: "Kilobytes", short: "KB", factor: 1000 },
      { id: "mb", label: "Megabytes", short: "MB", factor: 1000000 },
      { id: "gb", label: "Gigabytes", short: "GB", factor: 1000000000 },
      { id: "tb", label: "Terabytes", short: "TB", factor: 1000000000000 },
      { id: "kib", label: "Kibibytes", short: "KiB", factor: 1024 },
      { id: "mib", label: "Mebibytes", short: "MiB", factor: 1048576 },
      { id: "gib", label: "Gibibytes", short: "GiB", factor: 1073741824 },
      { id: "tib", label: "Tebibytes", short: "TiB", factor: 1099511627776 }
    ]
  },
  {
    id: "energy",
    label: "Energy",
    baseUnit: "kilojoule",
    units: [
      { id: "j", label: "Joules", short: "J", factor: 0.001 },
      { id: "kj", label: "Kilojoules", short: "kJ", factor: 1 },
      { id: "cal", label: "Calories", short: "cal", factor: 0.004184 },
      { id: "kcal", label: "Kilocalories", short: "kcal", factor: 4.184 },
      { id: "wh", label: "Watt-hours", short: "Wh", factor: 3.6 },
      { id: "kwh", label: "Kilowatt-hours", short: "kWh", factor: 3600 },
      { id: "btu", label: "British thermal units", short: "BTU", factor: 1.05505585262 }
    ]
  },
  {
    id: "power",
    label: "Power",
    baseUnit: "watt",
    units: [
      { id: "w", label: "Watts", short: "W", factor: 1 },
      { id: "kw", label: "Kilowatts", short: "kW", factor: 1000 },
      { id: "hp", label: "Horsepower", short: "hp", factor: 745.699872 },
      { id: "btuh", label: "BTU per hour", short: "BTU/h", factor: 0.29307107 }
    ]
  },
  {
    id: "time",
    label: "Time",
    baseUnit: "second",
    units: [
      { id: "ms", label: "Milliseconds", short: "ms", factor: 0.001 },
      { id: "sec", label: "Seconds", short: "sec", factor: 1 },
      { id: "min", label: "Minutes", short: "min", factor: 60 },
      { id: "hr", label: "Hours", short: "hr", factor: 3600 },
      { id: "day", label: "Days", short: "day", factor: 86400 },
      { id: "week", label: "Weeks", short: "week", factor: 604800 }
    ]
  },
  {
    id: "pace",
    label: "Running Pace",
    baseUnit: "seconds per kilometer",
    units: [
      { id: "minkm", label: "Minutes per kilometer", short: "min/km", toBase: (v) => v * 60, fromBase: (v) => v / 60 },
      { id: "minmi", label: "Minutes per mile", short: "min/mi", toBase: (v) => v * 60 / 1.609344, fromBase: (v) => v * 1.609344 / 60 },
      { id: "kmh", label: "Kilometers per hour", short: "km/h", toBase: (v) => (v === 0 ? Number.NaN : 3600 / v), fromBase: (v) => (v === 0 ? Number.NaN : 3600 / v) },
      { id: "mph", label: "Miles per hour", short: "mph", toBase: (v) => (v === 0 ? Number.NaN : 3600 / (v * 1.609344)), fromBase: (v) => (v === 0 ? Number.NaN : 3600 / v / 1.609344) }
    ]
  }
];

export const corePresets = [
  { id: "cm-in", title: "cm to inches", group: "length", from: "cm", to: "in", input: "30", formula: "in = cm / 2.54", tags: ["length", "clothes", "height"] },
  { id: "in-cm", title: "inches to cm", group: "length", from: "in", to: "cm", input: "12", formula: "cm = in x 2.54", tags: ["length", "screen", "fabric"] },
  { id: "m-ft", title: "meters to feet", group: "length", from: "m", to: "ft", input: "1.8", formula: "ft = m x 3.28084", tags: ["height", "room", "travel"] },
  { id: "km-mi", title: "kilometers to miles", group: "length", from: "km", to: "mi", input: "10", formula: "mi = km x 0.621371", tags: ["travel", "distance"] },
  { id: "kg-lb", title: "kg to lbs", group: "weight", from: "kg", to: "lb", input: "70", formula: "lb = kg x 2.20462", tags: ["weight", "shipping", "fitness"] },
  { id: "lb-kg", title: "lbs to kg", group: "weight", from: "lb", to: "kg", input: "150", formula: "kg = lb x 0.453592", tags: ["weight", "shipping"] },
  { id: "g-oz", title: "grams to ounces", group: "weight", from: "g", to: "oz", input: "250", formula: "oz = g / 28.3495", tags: ["cooking", "food"] },
  { id: "oz-g", title: "ounces to grams", group: "weight", from: "oz", to: "g", input: "8", formula: "g = oz x 28.3495", tags: ["cooking", "food"] },
  { id: "c-f", title: "Celsius to Fahrenheit", group: "temperature", from: "c", to: "f", input: "20", formula: "F = C x 9/5 + 32", tags: ["weather", "oven", "temperature"] },
  { id: "f-c", title: "Fahrenheit to Celsius", group: "temperature", from: "f", to: "c", input: "350", formula: "C = (F - 32) x 5/9", tags: ["oven", "weather"] },
  { id: "sqft-sqm", title: "square feet to square meters", group: "area", from: "sqft", to: "sqm", input: "120", formula: "sq m = sq ft x 0.092903", tags: ["room", "home", "real estate"] },
  { id: "sqm-sqft", title: "square meters to square feet", group: "area", from: "sqm", to: "sqft", input: "25", formula: "sq ft = sq m x 10.7639", tags: ["room", "flooring"] },
  { id: "acre-ha", title: "acres to hectares", group: "area", from: "acre", to: "hectare", input: "5", formula: "ha = acre x 0.404686", tags: ["land", "farm"] },
  { id: "l-gal", title: "liters to gallons", group: "volume", from: "l", to: "gal", input: "10", formula: "gal = L x 0.264172", tags: ["volume", "fuel", "pool"] },
  { id: "gal-l", title: "gallons to liters", group: "volume", from: "gal", to: "l", input: "3", formula: "L = gal x 3.78541", tags: ["volume", "fuel"] },
  { id: "cup-ml", title: "cups to ml", group: "volume", from: "cup", to: "ml", input: "2", formula: "ml = cups x 236.588", tags: ["cooking", "recipe"] },
  { id: "tbsp-ml", title: "tablespoons to ml", group: "volume", from: "tbsp", to: "ml", input: "3", formula: "ml = tbsp x 14.7868", tags: ["cooking", "recipe"] },
  { id: "kpa-psi", title: "kPa to psi", group: "pressure", from: "kpa", to: "psi", input: "220", formula: "psi = kPa x 0.145038", tags: ["tire", "pressure"] },
  { id: "psi-kpa", title: "psi to kPa", group: "pressure", from: "psi", to: "kpa", input: "32", formula: "kPa = psi x 6.89476", tags: ["tire", "pressure"] },
  { id: "bar-psi", title: "bar to psi", group: "pressure", from: "bar", to: "psi", input: "2.4", formula: "psi = bar x 14.5038", tags: ["tire", "bike"] },
  { id: "mph-kmh", title: "mph to km/h", group: "speed", from: "mph", to: "kmh", input: "60", formula: "km/h = mph x 1.60934", tags: ["speed", "travel"] },
  { id: "kmh-mph", title: "km/h to mph", group: "speed", from: "kmh", to: "mph", input: "100", formula: "mph = km/h x 0.621371", tags: ["speed", "travel"] },
  { id: "knots-mph", title: "knots to mph", group: "speed", from: "knot", to: "mph", input: "20", formula: "mph = knots x 1.15078", tags: ["boat", "wind"] },
  { id: "mpg-l100", title: "mpg US to L/100 km", group: "fuel", from: "mpg-us", to: "l100km", input: "30", formula: "L/100 km = 235.214583 / mpg", tags: ["car", "fuel"] },
  { id: "l100-mpg", title: "L/100 km to mpg US", group: "fuel", from: "l100km", to: "mpg-us", input: "7.5", formula: "mpg = 235.214583 / L/100 km", tags: ["car", "fuel"] },
  { id: "gb-gib", title: "GB to GiB", group: "data", from: "gb", to: "gib", input: "512", formula: "GiB = GB x 1,000,000,000 / 1,073,741,824", tags: ["storage", "computer"] },
  { id: "kwh-btu", title: "kWh to BTU", group: "energy", from: "kwh", to: "btu", input: "1", formula: "BTU = kWh x 3412.14", tags: ["energy", "heating"] },
  { id: "btuh-kw", title: "BTU/h to kW", group: "power", from: "btuh", to: "kw", input: "12000", formula: "kW = BTU/h x 0.000293071", tags: ["air conditioner", "hvac"] },
  { id: "minmi-minkm", title: "min/mi to min/km", group: "pace", from: "minmi", to: "minkm", input: "8", formula: "min/km = min/mi / 1.60934", tags: ["running", "pace"] }
];

export const contextCards = [
  {
    id: "room",
    title: "Room size converter",
    eyebrow: "Home and real estate",
    description: "Turn length and width into square feet, square meters, acres, and pyeong with flooring overage.",
    group: "area",
    from: "sqft",
    to: "sqm",
    input: "120",
    tags: ["room", "flooring", "rent", "renovation"]
  },
  {
    id: "cooking",
    title: "Cooking measurement converter",
    eyebrow: "Recipe and kitchen",
    description: "Convert cups, tablespoons, ml, grams, and ounces with ingredient density for flour, sugar, butter, oil, and more.",
    group: "volume",
    from: "cup",
    to: "ml",
    input: "2",
    tags: ["cooking", "recipe", "baking"]
  },
  {
    id: "tire",
    title: "Tire pressure converter",
    eyebrow: "Car and bike",
    description: "Switch between psi, kPa, and bar with quick pressure readings for car tires, bike tires, and inflators.",
    group: "pressure",
    from: "psi",
    to: "kpa",
    input: "35",
    tags: ["tire", "bike", "pressure"]
  },
  {
    id: "fabric",
    title: "Fabric measurement converter",
    eyebrow: "Sewing and crafts",
    description: "Convert fabric length, bolt width, square yards, square meters, inches, centimeters, meters, and yards.",
    group: "length",
    from: "yd",
    to: "m",
    input: "3",
    tags: ["fabric", "sewing", "craft"]
  },
  {
    id: "shipping",
    title: "Shipping weight converter",
    eyebrow: "Logistics",
    description: "Compare actual weight with dimensional weight for common lb/in and kg/cm carrier divisors.",
    group: "weight",
    from: "kg",
    to: "lb",
    input: "5",
    tags: ["shipping", "freight", "parcel"]
  },
  {
    id: "fuel",
    title: "Fuel economy converter",
    eyebrow: "Driving",
    description: "Convert mpg US, mpg Imperial, L/100 km, and km/L without live data or server calls.",
    group: "fuel",
    from: "mpg-us",
    to: "l100km",
    input: "30",
    tags: ["fuel", "car", "travel"]
  },
  {
    id: "paint",
    title: "Paint coverage converter",
    eyebrow: "Renovation",
    description: "Estimate gallons and liters from wall dimensions, coats, doors, windows, and coverage rate.",
    group: "volume",
    from: "gal",
    to: "l",
    input: "1",
    tags: ["paint", "walls", "home"]
  },
  {
    id: "pace",
    title: "Running pace converter",
    eyebrow: "Fitness",
    description: "Move between min/km, min/mi, km/h, and mph for treadmill, road race, and training plans.",
    group: "pace",
    from: "minmi",
    to: "minkm",
    input: "8",
    tags: ["running", "fitness", "speed"]
  },
  {
    id: "pool",
    title: "Pool volume converter",
    eyebrow: "Outdoor",
    description: "Convert pool dimensions into gallons, liters, and cubic meters for water treatment planning.",
    group: "volume",
    from: "m3",
    to: "gal",
    input: "25",
    tags: ["pool", "water", "volume"]
  },
  {
    id: "storage",
    title: "Digital storage converter",
    eyebrow: "Tech",
    description: "Compare decimal storage units with binary units like GB, GiB, TB, and TiB.",
    group: "data",
    from: "gb",
    to: "gib",
    input: "512",
    tags: ["computer", "storage", "files"]
  },
  {
    id: "hvac",
    title: "HVAC power converter",
    eyebrow: "Heating and cooling",
    description: "Convert BTU/h, watts, kilowatts, and horsepower for air conditioners and heaters.",
    group: "power",
    from: "btuh",
    to: "kw",
    input: "12000",
    tags: ["hvac", "air conditioner", "heater"]
  },
  {
    id: "land",
    title: "Land area converter",
    eyebrow: "Property",
    description: "Convert acres, hectares, square meters, square feet, and pyeong for lots and listings.",
    group: "area",
    from: "acre",
    to: "hectare",
    input: "1",
    tags: ["land", "property", "farm"]
  }
];

export const ingredientProfiles = [
  { id: "water", label: "Water", density: 1 },
  { id: "milk", label: "Milk", density: 1.03 },
  { id: "flour", label: "All-purpose flour", density: 0.529 },
  { id: "sugar", label: "Granulated sugar", density: 0.845 },
  { id: "brown-sugar", label: "Packed brown sugar", density: 0.721 },
  { id: "butter", label: "Butter", density: 0.959 },
  { id: "oil", label: "Cooking oil", density: 0.92 },
  { id: "honey", label: "Honey", density: 1.42 },
  { id: "rice", label: "Uncooked rice", density: 0.85 },
  { id: "cocoa", label: "Cocoa powder", density: 0.42 }
];

export const cookingUnits = [
  { id: "g", label: "grams", type: "mass", grams: 1 },
  { id: "oz", label: "ounces", type: "mass", grams: 28.349523125 },
  { id: "ml", label: "milliliters", type: "volume", ml: 1 },
  { id: "l", label: "liters", type: "volume", ml: 1000 },
  { id: "tsp", label: "teaspoons", type: "volume", ml: 4.92892159375 },
  { id: "tbsp", label: "tablespoons", type: "volume", ml: 14.78676478125 },
  { id: "cup", label: "US cups", type: "volume", ml: 236.5882365 },
  { id: "floz", label: "US fluid ounces", type: "volume", ml: 29.5735295625 }
];

export const shippingDivisors = [
  { id: "139", label: "139 in3/lb - express parcels", system: "imperial", divisor: 139 },
  { id: "166", label: "166 in3/lb - economy parcels", system: "imperial", divisor: 166 },
  { id: "5000", label: "5000 cm3/kg - express parcels", system: "metric", divisor: 5000 },
  { id: "6000", label: "6000 cm3/kg - economy parcels", system: "metric", divisor: 6000 }
];

export function getGroup(groupId) {
  return conversionGroups.find((group) => group.id === groupId) || conversionGroups[0];
}

export function getUnit(group, unitId) {
  return group.units.find((unit) => unit.id === unitId) || group.units[0];
}

export function convertValue(groupId, fromId, toId, value) {
  const group = getGroup(groupId);
  const from = getUnit(group, fromId);
  const to = getUnit(group, toId);
  const amount = Number(value);

  if (!Number.isFinite(amount)) {
    return Number.NaN;
  }

  const base = from.toBase ? from.toBase(amount) : amount * from.factor;
  return to.fromBase ? to.fromBase(base) : base / to.factor;
}

export function formatNumber(value, digits = 6) {
  if (!Number.isFinite(value)) {
    return "";
  }

  if (Math.abs(value) > 0 && Math.abs(value) < 0.000001) {
    return value.toExponential(4);
  }

  const abs = Math.abs(value);
  const maximumFractionDigits =
    abs >= 10000 ? 2 : abs >= 1000 ? 3 : abs >= 100 ? 4 : abs >= 10 ? 5 : digits;

  return Number(value.toPrecision(12)).toLocaleString("en-US", {
    maximumFractionDigits
  });
}
